import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import GooglePlacesInput from "@/components/shared/GooglePlacesInput";
import {
  User,
  Mail,
  Phone,
  Building,
  Briefcase,
  Instagram,
  Linkedin,
  Edit3,
  Check,
  X,
  Plus,
  Trash2,
  MapPin,
  Camera,
  Settings,
  ExternalLink,
  Music,
  Sparkles,
  ChevronRight,
  RefreshCw,
  Lock,
  Star,
  Globe,
  Shield,
  Users,
  TrendingUp,
  Link2,
  Loader2,
  Calendar,
  Headphones,
  HeartHandshake,
  Rocket
} from "lucide-react";
import EarlyAccessModal from "@/components/EarlyAccessModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default fallback role options - host-relevant labels (R25).
// Stale tech-startup labels (Founder/CEO, Creative Director, Designer, Developer, Investor, etc.)
// were stripped per master spec. Categories should drive the actual options via the API.
const DEFAULT_ROLE_OPTIONS = [
  { id: "host", label: "Host" },
  { id: "client", label: "Client" },
  { id: "guest", label: "Guest" },
  { id: "team", label: "Team" },
  { id: "press", label: "Press" },
  { id: "other", label: "Other" }
];

// Static city fallback
const POPULAR_CITIES = [
  "Los Angeles, CA", "New York, NY", "Miami, FL", "San Francisco, CA",
  "Chicago, IL", "Atlanta, GA", "Dallas, TX", "Houston, TX",
  "Seattle, WA", "Denver, CO", "Austin, TX", "Las Vegas, NV"
];

// Email Item Component
const EmailItem = ({ email, isPrimary, onRemove, onSetPrimary, canRemove }) => (
  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group">
    <Mail className="w-4 h-4 text-gray-400" />
    <span className="flex-1 text-sm truncate">{email}</span>
    {isPrimary && (
      <span className="text-[10px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">Primary</span>
    )}
    {!isPrimary && (
      <button
        onClick={onSetPrimary}
        className="text-[10px] text-gray-500 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        Set Primary
      </button>
    )}
    {canRemove && (
      <button onClick={onRemove} className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        <X className="w-3 h-3 text-red-500" />
      </button>
    )}
  </div>
);

// Phone Item Component
const PhoneItem = ({ phone, isPrimary, onRemove, onSetPrimary, canRemove }) => (
  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg group">
    <Phone className="w-4 h-4 text-gray-400" />
    <span className="flex-1 text-sm">{phone}</span>
    {isPrimary && (
      <span className="text-[10px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">Primary</span>
    )}
    {!isPrimary && (
      <button
        onClick={onSetPrimary}
        className="text-[10px] text-gray-500 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        Set Primary
      </button>
    )}
    {canRemove && (
      <button onClick={onRemove} className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        <X className="w-3 h-3 text-red-500" />
      </button>
    )}
  </div>
);

// Profile Image Upload Component
const ProfileImageUpload = ({ currentImage, userName, onImageChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/user/profile-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Upload failed");
      
      const data = await res.json();
      onImageChange(data.image_url);
      toast.success("Profile image updated!");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="relative group">
      <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-gray-700 to-black flex items-center justify-center">
        {currentImage ? (
          <img src={currentImage} alt={userName} className="w-full h-full object-cover" />
        ) : (
          <span className="text-white text-2xl font-bold">
            {userName?.charAt(0)?.toUpperCase() || "P"}
          </span>
        )}
      </div>
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
      >
        <Camera className={`w-6 h-6 text-white ${uploading ? 'animate-pulse' : ''}`} />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

// City Search Component with Autocomplete
const CitySearchInput = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (val) => {
    setInputValue(val);
    if (val.length >= 2) {
      try {
        // Iter 9.29: restrict to (cities) so results come back as "City, State, Country"
        const res = await fetch(`${API}/places/autocomplete?input=${encodeURIComponent(val)}&types=(cities)`);
        const data = await res.json();
        if (data.predictions?.length > 0) {
          setSuggestions(data.predictions);
        } else {
          // Fallback to static cities
          const filtered = POPULAR_CITIES.filter(c => c.toLowerCase().includes(val.toLowerCase()));
          setSuggestions(filtered.map(c => ({ description: c })));
        }
        setShowSuggestions(true);
      } catch (err) {
        const filtered = POPULAR_CITIES.filter(c => c.toLowerCase().includes(val.toLowerCase()));
        setSuggestions(filtered.map(c => ({ description: c })));
        setShowSuggestions(true);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const selectCity = (city) => {
    const cityName = typeof city === 'string' ? city : city.description;
    setInputValue(cityName);
    onChange(cityName);
    setShowSuggestions(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
          onBlur={() => onChange(inputValue)}
          placeholder="Search city..."
          className="pl-9"
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((city, i) => (
            <button
              key={i}
              type="button"
              onClick={() => selectCity(city)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              <MapPin className="w-3 h-3 text-gray-400" />
              {city.description || city}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Tap In app cover image - use local logo for sync with admin
import { TAP_IN_LOGO as TAP_IN_COVER } from "@/lib/integrationConfig";

// Integration Card Component
const IntegrationCard = ({ name, icon: Icon, connected, username, onConnect, onDisconnect, description, comingSoon, coverImage }) => (
  <div className={`p-4 rounded-xl border ${connected ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-white'}`}>
    <div className="flex items-center gap-3">
      {coverImage ? (
        <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
          <img src={coverImage} alt={name} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          name === 'Instagram' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
          'bg-gray-800'
        }`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm">{name}</h4>
          {connected && (
            <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
              <Check className="w-3 h-3" /> Connected
            </span>
          )}
          {comingSoon && (
            <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Coming Soon</span>
          )}
        </div>
        {connected && username && (
          <p className="text-xs text-gray-500">@{username}</p>
        )}
        {!connected && !comingSoon && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
      {!comingSoon && (
        <Button
          variant={connected ? "outline" : "default"}
          size="sm"
          onClick={connected ? onDisconnect : onConnect}
          className="h-8 text-xs"
        >
          {connected ? "Disconnect" : "Connect"}
        </Button>
      )}
    </div>
  </div>
);

// Host Experience / Provider CTA Card
const ProviderWaitlistCard = ({ user, onDismiss, onJoinWaitlist, meTemplate }) => {
  // R: hide entirely when user is already a host / pilot / provider / has any pending host event.
  // "Become a Provider" should NEVER show to someone already in the host pipeline.
  const isHost = user?.is_host || user?.is_provider || user?.is_pilot
    || user?.role === 'admin' || user?.role === 'super_admin' || user?.is_legacy
    || (Array.isArray(user?.host_orgs) && user.host_orgs.length > 0)
    || user?.dashboard_status === 'pending_review' || user?.dashboard_status === 'active'
    || user?.plan === 'legacy_pilot_free';
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('phygital_dismiss_host_cta') === 'true';
  });

  // Already a host: hide the CTA entirely (was previously showing "Host Your Own Experience" card)
  if (isHost) return null;

  // Not a host and previously dismissed the waitlist CTA: hide
  if (dismissed) return null;
  return (
    <Card className="bg-black border-gray-800" data-testid="provider-waitlist-cta">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm text-white">Become a Provider</h4>
            <p className="text-xs text-gray-400 mt-1">
              Join our network of beauty & wellness providers. Get your own booking page, client management, and more.
            </p>
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm" 
                className="h-8 text-xs bg-white text-black hover:bg-gray-100"
                onClick={onJoinWaitlist}
              >
                Join Waitlist
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-400 hover:text-white hover:bg-white/10" onClick={onDismiss}>
                Not Now
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

import { isSuperAdminEmail } from '@/config/platform';

// Portal Quick Access Section - Shows only portals that are set up and user has access to
const PortalQuickAccess = ({ user }) => {
  const navigate = useNavigate();
  const [portalAccess, setPortalAccess] = useState([]);
  const [investorPortalId, setInvestorPortalId] = useState(null);
  const [pilotEvents, setPilotEvents] = useState([]);
  
  const userRole = user?.role || '';
  const userEmail = user?.email || '';
  
  const hasAdminAccess = userRole === 'admin' || 
                         userRole === 'super_admin' ||
                         user?.is_super_admin ||
                         isSuperAdminEmail(userEmail);
  
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/user/portal-access`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setPortalAccess(data.portals || []);
          setPilotEvents(data.pilot_events || []);
          
          // If user has investor access, fetch their investor portal ID
          if ((data.portals || []).includes('investor')) {
            try {
              const invRes = await fetch(`${API}/api/user/my-investor-portal`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
              });
              if (invRes.ok) {
                const invData = await invRes.json();
                if (invData.has_portal && invData.portal_id) {
                  setInvestorPortalId(invData.portal_id);
                }
              }
            } catch {}
          }
        }
      } catch (err) {}
    };
    load();
  }, []);
  
  const portalDefs = [
    { id: 'admin', name: 'Admin', icon: Shield, color: 'bg-red-100 text-red-600', href: '/admin', hasAccess: hasAdminAccess, description: 'Platform administration' },
    { id: 'investor', name: 'Investor Portal', icon: TrendingUp, color: 'bg-emerald-100 text-emerald-600', href: investorPortalId ? `/investor/portal/${investorPortalId}` : '/investor', hasAccess: portalAccess.includes('investor'), description: 'Updates, pitch deck & projections' },
    { id: 'team', name: 'Team Portal', icon: Users, color: 'bg-blue-100 text-blue-600', href: '/portal/team', hasAccess: portalAccess.includes('team'), description: 'Team updates & collaboration' },
    { id: 'network', name: 'Network Portal', icon: Globe, color: 'bg-purple-100 text-purple-600', href: '/portal/network', hasAccess: portalAccess.includes('network'), description: 'Network updates & advocate tools' },
  ].filter(p => p.hasAccess);

  // Add pilot portal links
  if (portalAccess.includes('pilot') && pilotEvents.length > 0) {
    pilotEvents.forEach(pe => {
      portalDefs.push({
        id: `pilot_${pe.category}`, name: `Pilot Dashboard - ${(pe.category || '').charAt(0).toUpperCase() + (pe.category || '').slice(1)}`, icon: Rocket || Star, color: 'bg-orange-100 text-orange-600', href: `/host/v2/${pe.event_id}`, hasAccess: true, description: `${(pe.category || '').charAt(0).toUpperCase() + (pe.category || '').slice(1)} host dashboard`
      });
    });
  }

  // Check advocate status
  const [isAdvocate, setIsAdvocate] = useState(false);
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/user/advocate-status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) { const d = await res.json(); setIsAdvocate(d.is_advocate); }
      } catch (err) {}
    };
    load();
  }, []);

  if (isAdvocate) {
    portalDefs.push({
      id: 'advocate', name: 'Advocate Dashboard', icon: HeartHandshake, color: 'bg-teal-100 text-teal-600', href: '/advocate', hasAccess: true, description: 'Referrals, earnings & promote'
    });
  }
  
  if (portalDefs.length === 0) return null;
  
  return (
    <Card className="rounded-xl border-gray-200" data-testid="portal-quick-access">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-500" />
            Quick Access
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {portalDefs.length} portal{portalDefs.length > 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {portalDefs.map(portal => {
          const Icon = portal.icon;
          return (
            <button
              key={portal.id}
              onClick={() => navigate(portal.href)}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left group"
              data-testid={`portal-link-${portal.id}`}
            >
              <div className={`w-10 h-10 rounded-lg ${portal.color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm">{portal.name}</h4>
                <p className="text-xs text-gray-500 truncate">{portal.description}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Main Settings Tab Component
export const SettingsTab = ({ user, onUserUpdate, viewMode, meTemplate }) => {
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showProviderCard, setShowProviderCard] = useState(true);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  
  // Dynamic role options - fetched from API based on user's category access
  const [roleOptions, setRoleOptions] = useState(DEFAULT_ROLE_OPTIONS);
  const [roleOptionsLoading, setRoleOptionsLoading] = useState(true);
  
  // Organization state
  const [organizations, setOrganizations] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgFormData, setOrgFormData] = useState({ name: "", description: "", website: "" });
  const [orgLogoUploading, setOrgLogoUploading] = useState(false);
  const orgLogoInputRef = useRef(null);
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [newOrgData, setNewOrgData] = useState({ name: "", description: "", website: "" });
  const [creatingOrg, setCreatingOrg] = useState(false);
  
  // Verification states
  // Phone verification state (Sinch SMS)
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [pendingPhone, setPendingPhone] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  
  // Form state for About section
  const [aboutData, setAboutData] = useState({
    first_name: "",
    last_name: "",
    name: "",
    email: "",
    phone: "",
    city: "",
    company: "",
    title: "",
    role_type: "",
    instagram: "",
    tiktok: "",
    linkedin: "",
    // Personal socials (personal accounts)
    personal_socials: {
      instagram: "",
      tiktok: "",
      linkedin: "",
      twitter: ""
    },
    // Business socials (brand/company accounts)
    business_socials: {
      instagram: "",
      website: "",
      yelp: "",
      google_business: "",
      facebook: "",
      linkedin: ""
    }
  });

  // Multiple emails/phones state
  const [emails, setEmails] = useState([]);
  const [phones, setPhones] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");

  // Fetch dynamic role options based on user's pilot/category access
  useEffect(() => {
    const fetchRoleOptions = async () => {
      try {
        const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
        const res = await fetch(`${API}/role-options/for-user`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.options && data.options.length > 0) {
            setRoleOptions(data.options);
          }
        }
      } catch (err) {
        console.error("Failed to fetch role options:", err);
        // Keep default options on error
      } finally {
        setRoleOptionsLoading(false);
      }
    };
    
    fetchRoleOptions();
  }, []);

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setAboutData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        city: user.city || "",
        company: user.company || "",
        title: user.title || "",
        role_type: user.role_type || user.role_title || "",
        instagram: user.instagram || user.instagram_username || "",
        tiktok: user.tiktok || "",
        linkedin: user.linkedin || "",
        // Personal socials
        personal_socials: user.personal_socials || {
          instagram: "",
          tiktok: "",
          linkedin: "",
          twitter: ""
        },
        // Business socials
        business_socials: user.business_socials || {
          instagram: "",
          website: "",
          yelp: "",
          google_business: "",
          facebook: "",
          linkedin: ""
        }
      });
      
      // Initialize emails array - normalize Iter 9.47.v object form
      // ({value, label, is_primary, is_verified, added_at}) -> plain strings.
      const normalize = (v) => {
        if (!v) return "";
        if (typeof v === "string") return v;
        if (typeof v === "object") return v.value || v.email || v.phone || "";
        return String(v);
      };
      const rawEmails = Array.isArray(user.emails) ? user.emails : (user.email ? [user.email] : []);
      const userEmails = rawEmails.map(normalize).filter(Boolean);
      setEmails(userEmails);

      // Initialize phones array
      const rawPhones = Array.isArray(user.phones) ? user.phones : (user.phone ? [user.phone] : []);
      const userPhones = rawPhones.map(normalize).filter(Boolean);
      setPhones(userPhones);
    }
  }, [user]);

  // Fetch user's organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
        if (!token) return;
        
        const res = await fetch(`${API}/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const orgs = await res.json();
          // Deduplicate organizations by org_id to prevent showing duplicates
          const uniqueOrgs = Array.isArray(orgs) 
            ? orgs.filter((org, index, self) => 
                index === self.findIndex(o => o.org_id === org.org_id)
              )
            : [];
          setOrganizations(uniqueOrgs);
        }
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
      }
    };
    
    fetchOrganizations();
  }, []);

  // Handle organization logo upload
  const handleOrgLogoUpload = async (e, orgId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    setOrgLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/organizations/${orgId}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) throw new Error("Upload failed");
      
      const data = await res.json();
      
      // Update the organization in state
      setOrganizations(prev => prev.map(org => 
        org.org_id === orgId ? { ...org, logo: data.logo_url } : org
      ));
      
      toast.success("Host logo updated!");
    } catch (err) {
      console.error("Logo upload error:", err);
      toast.error("Failed to upload logo");
    } finally {
      setOrgLogoUploading(false);
    }
  };

  // Save organization changes
  const handleSaveOrganization = async () => {
    if (!editingOrg) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/organizations/${editingOrg.org_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: orgFormData.name,
          description: orgFormData.description,
          website: orgFormData.website
        })
      });
      
      if (!res.ok) throw new Error("Failed to save");
      
      const updatedOrg = await res.json();
      
      // Update local state
      setOrganizations(prev => prev.map(org => 
        org.org_id === editingOrg.org_id ? { ...org, ...updatedOrg } : org
      ));
      
      toast.success("Host updated!");
      setEditingOrg(null);
    } catch (err) {
      console.error("Save org error:", err);
      toast.error("Failed to update organization");
    } finally {
      setSaving(false);
    }
  };

  // Create new organization
  const handleCreateOrganization = async () => {
    if (!newOrgData.name.trim()) {
      toast.error("Host name is required");
      return;
    }
    
    setCreatingOrg(true);
    try {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/organizations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newOrgData.name.trim(),
          description: newOrgData.description.trim(),
          website: newOrgData.website.trim()
        })
      });
      
      if (!res.ok) throw new Error("Failed to create organization");
      
      const data = await res.json();
      
      // Add the new org to local state with the owner role
      const newOrg = {
        ...data.organization,
        my_role: "owner"
      };
      setOrganizations(prev => [...prev, newOrg]);
      
      // Update user state to reflect the new primary org
      if (onUserUpdate) {
        onUserUpdate({
          ...user,
          org_name: newOrgData.name.trim(),
          org_id: data.org_id
        });
      }
      
      toast.success("Host created! It will now appear as your host profile.");
      setShowAddOrg(false);
      setNewOrgData({ name: "", description: "", website: "" });
    } catch (err) {
      console.error("Create org error:", err);
      toast.error("Failed to create organization");
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleSaveAbout = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/user/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...aboutData,
          role_title: aboutData.role_type,
          emails: emails,
          phones: phones
        })
      });

      if (!res.ok) throw new Error("Failed to save");
      
      const data = await res.json();
      toast.success("Profile updated!");
      setEditingSection(null);
      if (onUserUpdate) onUserUpdate(data.user || data);
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const saveContactInfo = async (newEmails, newPhones) => {
    try {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/user/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          emails: newEmails,
          phones: newPhones
        })
      });

      if (!res.ok) throw new Error("Failed to save");
      
      const data = await res.json();
      if (onUserUpdate) onUserUpdate(data.user || data);
      return true;
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save contact info");
      return false;
    }
  };

  // Add email via Google OAuth (for Gmail) or direct add (for other emails)
  const initiateEmailVerification = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error("Please enter a valid email");
      return;
    }
    if (emails.length >= 5) {
      toast.error("Maximum 5 emails allowed");
      return;
    }
    if (emails.includes(newEmail)) {
      toast.error("Email already added");
      return;
    }
    if (!emailConsent) {
      toast.error("Please agree to the terms to add an email");
      return;
    }
    
    // For Gmail addresses, use Google OAuth to verify ownership
    if (newEmail.toLowerCase().endsWith('@gmail.com')) {
      // Store pending email in localStorage for after OAuth redirect
      localStorage.setItem('pending_add_email', newEmail);
      const redirectUrl = window.location.origin + '/me?tab=settings&email_added=true';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&hint=${encodeURIComponent(newEmail)}`;
    } else {
      // For non-Gmail, add directly with consent
      addEmailDirectly(newEmail);
    }
  };

  const addEmailDirectly = async (emailToAdd) => {
    const newEmails = [...emails, emailToAdd];
    const saved = await saveContactInfo(newEmails, phones);
    if (saved) {
      setEmails(newEmails);
      setNewEmail("");
      setEmailConsent(false);
      toast.success("Email added!");
    }
  };

  // Check for OAuth redirect callback - runs once on mount
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const emailAdded = urlParams.get('email_added');
      const pendingFlow = localStorage.getItem('pending_add_email_flow');
      const originalToken = localStorage.getItem('original_session_token');
      const pendingEmail = localStorage.getItem('pending_add_email');
      
      if (emailAdded === 'true' && pendingFlow === 'true') {
        // Clean up URL immediately
        window.history.replaceState({}, '', window.location.pathname + '?tab=settings');
        
        // Clean up localStorage
        localStorage.removeItem('pending_add_email_flow');
        localStorage.removeItem('pending_add_email');
        localStorage.removeItem('original_session_token');
        
        try {
          // Get the current session token (this is the NEW Google account's session)
          const newToken = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
          
          // Get the Google email from the NEW session (this is what Google authenticated)
          const newSessionRes = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          const newSessionData = await newSessionRes.json();
          const googleEmail = newSessionData.email;
          
          if (!googleEmail) {
            toast.error("Could not retrieve Gmail address from Google");
            return;
          }
          
          // Now we need to add this email to the ORIGINAL user's account
          // If we have the original token, use it. Otherwise, the user may have been logged out.
          if (originalToken) {
            // Restore the original session
            localStorage.setItem("session_token", originalToken);
            
            // Get the original user's emails
            const origRes = await fetch(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${originalToken}` }
            });
            
            if (origRes.ok) {
              const origData = await origRes.json();
              const currentEmails = origData.emails || origData.linked_emails || [];
              const primaryEmail = origData.email;
              
              // Build the current emails list
              const allCurrentEmails = primaryEmail ? [primaryEmail, ...currentEmails.filter(e => e !== primaryEmail)] : currentEmails;
              
              // Check if this Gmail is already in the list
              if (allCurrentEmails.includes(googleEmail)) {
                toast.info(`${googleEmail} is already linked to your account`);
                setEmails(allCurrentEmails);
                return;
              }
              
              // Add the Gmail to the emails array
              const newEmails = [...allCurrentEmails, googleEmail];
              const res = await fetch(`${API}/user/profile`, {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${originalToken}`
                },
                body: JSON.stringify({ emails: newEmails })
              });
              
              if (res.ok) {
                setEmails(newEmails);
                toast.success(`Gmail ${googleEmail} added to your account!`);
                
                try {
                  const data = await res.json();
                  if (onUserUpdate && data) {
                    onUserUpdate(data.user || data);
                  }
                } catch (parseErr) {
                  if (onUserUpdate) {
                    onUserUpdate({ ...user, emails: newEmails });
                  }
                }
              } else {
                toast.error("Failed to save Gmail to your profile");
              }
            } else {
              // Original session expired, user needs to log in again
              toast.error("Your session expired. Please log in and try again.");
            }
          } else if (pendingEmail) {
            // Fallback: use the stored pending email if no original token
            // This handles the case where the user manually typed a Gmail to add
            const currentEmails = emails || [];
            if (!currentEmails.includes(pendingEmail)) {
              const newEmails = [...currentEmails, pendingEmail];
              const token = localStorage.getItem("session_token");
              const res = await fetch(`${API}/user/profile`, {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ emails: newEmails })
              });
              
              if (res.ok) {
                setEmails(newEmails);
                toast.success(`Email ${pendingEmail} added!`);
              }
            }
          } else {
            // No original token and no pending email - user is now logged in as the Google account
            toast.info(`Logged in as ${googleEmail}. To add this to a different account, please log in to that account first.`);
          }
        } catch (err) {
          console.error("OAuth callback error:", err);
          toast.error("Failed to add Gmail");
        }
      }
    };
    
    handleOAuthCallback();
  }, [onUserUpdate, user, emails]); // Include dependencies

  const addEmail = async () => {
    initiateEmailVerification();
  };

  const removeEmail = async (email) => {
    if (emails.length <= 1) {
      toast.error("Must have at least one email");
      return;
    }
    const newEmails = emails.filter(e => e !== email);
    const saved = await saveContactInfo(newEmails, phones);
    if (saved) {
      setEmails(newEmails);
      toast.success("Email removed");
    }
  };

  const setPrimaryEmail = async (email) => {
    const others = emails.filter(e => e !== email);
    const newEmails = [email, ...others];
    const saved = await saveContactInfo(newEmails, phones);
    if (saved) {
      setEmails(newEmails);
      setAboutData({...aboutData, email});
      toast.success("Primary email updated");
    }
  };

  // Start phone verification via Sinch SMS
  const initiatePhoneVerification = async () => {
    if (!newPhone || newPhone.length < 7) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (phones.length >= 3) {
      toast.error("Maximum 3 phone numbers allowed");
      return;
    }
    if (phones.includes(newPhone)) {
      toast.error("Phone already added");
      return;
    }
    
    try {
      // Send SMS verification code via Sinch (mocked for now)
      const res = await fetch(`${API}/auth/send-sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: newPhone })
      });
      
      if (!res.ok) throw new Error("Failed to send code");
      
      setPendingPhone(newPhone);
      setShowPhoneVerify(true);
      toast.success("Verification code sent!");
    } catch (err) {
      console.error("SMS error:", err);
      toast.error("Failed to send verification code");
    }
  };

  const verifyPhoneCode = async () => {
    if (!verifyCode || verifyCode.length < 4) {
      toast.error("Please enter the verification code");
      return;
    }
    
    setVerifying(true);
    try {
      // Verify code (mocked - accepts any 6 digit code for now)
      const res = await fetch(`${API}/auth/verify-sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: pendingPhone, code: verifyCode })
      });
      
      if (!res.ok) throw new Error("Invalid code");
      
      // Add phone after verification
      const newPhones = [...phones, pendingPhone];
      const saved = await saveContactInfo(emails, newPhones);
      if (saved) {
        setPhones(newPhones);
        setNewPhone("");
        setPendingPhone("");
        setVerifyCode("");
        setShowPhoneVerify(false);
        toast.success("Phone verified and added!");
      }
    } catch (err) {
      console.error("Verify error:", err);
      toast.error("Invalid verification code");
    } finally {
      setVerifying(false);
    }
  };

  const addPhone = async () => {
    initiatePhoneVerification();
  };

  const removePhone = async (phone) => {
    if (phones.length <= 1) {
      toast.error("Must have at least one phone");
      return;
    }
    const newPhones = phones.filter(p => p !== phone);
    const saved = await saveContactInfo(emails, newPhones);
    if (saved) {
      setPhones(newPhones);
      toast.success("Phone removed");
    }
  };

  const setPrimaryPhone = async (phone) => {
    const others = phones.filter(p => p !== phone);
    const newPhones = [phone, ...others];
    const saved = await saveContactInfo(emails, newPhones);
    if (saved) {
      setPhones(newPhones);
      setAboutData({...aboutData, phone});
      toast.success("Primary phone updated");
    }
  };

  const handleImageChange = (imageUrl) => {
    if (onUserUpdate) {
      // Update both field names for consistency across components
      onUserUpdate({...user, profile_image: imageUrl, profile_photo_url: imageUrl});
    }
  };

  const handleJoinWaitlist = () => {
    setShowEarlyAccessModal(true);
  };

  const roleLabel = roleOptions.find(r => r.id === aboutData.role_type)?.label;

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {/* Profile Header with Image */}
      {(!viewMode || viewMode === 'profile') && (<>
      <Card className="rounded-xl border-gray-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <ProfileImageUpload
              currentImage={user?.profile_image || user?.profile_photo_url}
              userName={user?.name}
              onImageChange={handleImageChange}
            />
            <div className="flex-1">
              <h2 className="text-xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                {user?.name || "Your Profile"}
              </h2>
              <p className="text-sm text-gray-500 font-mono">{user?.wallet_id}</p>
              {roleLabel && (
                <span className="inline-block mt-1 text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                  {roleLabel}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingSection('about')}
              className="h-9"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Edit Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* About Info Section */}
      <Card className="rounded-xl border-gray-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4 text-purple-500" />
              About
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setEditingSection('about')}>
              <Edit3 className="w-3 h-3 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Basic Info Display */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">First Name</label>
              <p className="text-sm font-medium">{aboutData.first_name || <span className="text-gray-400">Not set</span>}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Last Name</label>
              <p className="text-sm font-medium">{aboutData.last_name || <span className="text-gray-400">Not set</span>}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">City</label>
              <p className="text-sm">{aboutData.city || <span className="text-gray-400">Not set</span>}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Company</label>
              <p className="text-sm">{aboutData.company || <span className="text-gray-400">Not set</span>}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Title</label>
              <p className="text-sm">{aboutData.title || <span className="text-gray-400">Not set</span>}</p>
            </div>
          </div>

          {/* Role Badge */}
          {roleLabel && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Role</label>
              <span className="inline-block text-xs bg-purple-100 text-purple-600 px-3 py-1 rounded-full">
                {roleLabel}
              </span>
            </div>
          )}

          {/* Socials Display */}
          <div className="pt-3 border-t">
            <label className="text-xs text-gray-500 block mb-2">Social Links</label>
            <div className="flex flex-wrap gap-2">
              {aboutData.instagram && (
                <a href={`https://instagram.com/${aboutData.instagram}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-50 to-pink-50 text-xs text-pink-600 hover:from-purple-100 hover:to-pink-100">
                  <Instagram className="w-3 h-3" />
                  @{aboutData.instagram}
                </a>
              )}
              {aboutData.tiktok && (
                <a href={`https://tiktok.com/@${aboutData.tiktok}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs text-gray-700 hover:bg-gray-200">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  @{aboutData.tiktok}
                </a>
              )}
              {aboutData.linkedin && (
                <a href={`https://linkedin.com/in/${aboutData.linkedin}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-xs text-blue-600 hover:bg-blue-100">
                  <Linkedin className="w-3 h-3" />
                  in/{aboutData.linkedin}
                </a>
              )}
              {!aboutData.instagram && !aboutData.tiktok && !aboutData.linkedin && (
                <span className="text-xs text-gray-400">No social links added</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info Section */}
      <Card className="rounded-xl border-gray-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-purple-500" />
              Contact Info
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setEditingSection('contact')}>
              <Edit3 className="w-3 h-3 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Emails */}
          <div>
            <label className="text-xs text-gray-500 block mb-2">
              Email Addresses ({emails.length}/5)
            </label>
            <div className="space-y-2">
              {emails.map((email, i) => (
                <EmailItem
                  key={email}
                  email={email}
                  isPrimary={i === 0}
                  onRemove={() => removeEmail(email)}
                  onSetPrimary={() => setPrimaryEmail(email)}
                  canRemove={emails.length > 1}
                />
              ))}
              {emails.length === 0 && (
                <p className="text-sm text-gray-400">No email addresses</p>
              )}
            </div>
          </div>

          {/* Phones */}
          <div className="pt-3 border-t">
            <label className="text-xs text-gray-500 block mb-2">
              Phone Numbers ({phones.length}/3)
            </label>
            <div className="space-y-2">
              {phones.map((phone, i) => (
                <PhoneItem
                  key={phone}
                  phone={phone}
                  isPrimary={i === 0}
                  onRemove={() => removePhone(phone)}
                  onSetPrimary={() => setPrimaryPhone(phone)}
                  canRemove={phones.length > 1}
                />
              ))}
              {phones.length === 0 && (
                <p className="text-sm text-gray-400">No phone numbers</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      </>)}

      {/* Hosts Section - shows synced hosts from admin + user-created */}
      {(!viewMode || viewMode === 'organizations') && (<>
      <Card className="rounded-xl border-gray-200" data-testid="settings-organizations">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="w-4 h-4 text-purple-500" />
              Hosts
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddOrg(true)}
              className="h-8 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Host
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Hosts synced from your admin  - select one as your primary host.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {organizations.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
              <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-2">No hosts yet</p>
              <p className="text-xs text-gray-400 mb-4">Hosts assigned by admin will appear here automatically</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddOrg(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Your First Organization
              </Button>
            </div>
          ) : (
            organizations.map((org) => (
              <div
                key={org.org_id}
                className={`flex items-center gap-3 p-3 rounded-xl group transition-colors ${
                  user?.org_id === org.org_id 
                    ? 'bg-purple-50 border border-purple-200' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                data-testid={`org-card-${org.org_id}`}
              >
                {/* Org Logo */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center overflow-hidden">
                    {(org.logo || org.logo_url) ? (
                      <img src={org.logo || org.logo_url} alt={org.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-lg font-bold">
                        {org.name?.charAt(0)?.toUpperCase() || "O"}
                      </span>
                    )}
                  </div>
                  {user?.org_id === org.org_id && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Org Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-sm truncate">{org.name}</h4>
                    {user?.org_id === org.org_id && (
                      <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium" data-testid="org-primary-badge">Company</span>
                    )}
                    {org.synced_from_admin && (
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Synced</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{org.description || org.website || ""}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                      {org.my_role || "Member"}
                    </span>
                    {org.website && (
                      <a 
                        href={org.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[10px] text-purple-600 hover:text-purple-700 flex items-center gap-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-2.5 h-2.5" />
                        Website
                      </a>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  {/* Set as Company (Primary)  - always visible */}
                  {user?.org_id !== org.org_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`set-company-${org.org_id}`}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
                          const res = await fetch(`${API}/user/profile`, {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              org_id: org.org_id,
                              org_name: org.name,
                              org_logo: org.logo || org.logo_url
                            })
                          });
                          if (res.ok) {
                            if (onUserUpdate) {
                              onUserUpdate({ ...user, org_id: org.org_id, org_name: org.name, org_logo: org.logo || org.logo_url });
                            }
                            toast.success(`${org.name} set as primary organization`);
                          } else {
                            toast.error("Failed to update");
                          }
                        } catch (err) {
                          toast.error("Failed to update");
                        }
                      }}
                      className="h-7 px-3 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
                      title="Set as Company"
                    >
                      <Star className="w-3 h-3 mr-1" />
                      Set as Company
                    </Button>
                  )}
                  
                  {/* Edit Button - for owner/admin/super_admin */}
                  {(org.my_role === 'owner' || org.my_role === 'admin' || user?.role === 'super_admin' || user?.role === 'admin') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingOrg(org);
                        setOrgFormData({ 
                          name: org.name || "", 
                          description: org.description || "",
                          website: org.website || ""
                        });
                      }}
                      className="h-8 px-2"
                      title="Edit"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  
                  {/* Remove/Unlink */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!confirm(`Remove ${org.name} from your profile?`)) return;
                      try {
                        const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
                        // If this was the primary org, clear it from user profile
                        if (user?.org_id === org.org_id) {
                          await fetch(`${API}/user/profile`, {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              org_id: null,
                              org_name: null,
                              org_logo: null
                            })
                          });
                          if (onUserUpdate) {
                            onUserUpdate({ ...user, org_id: null, org_name: null, org_logo: null });
                          }
                        }
                        // Remove from org_members (user leaves the organization)
                        const leaveRes = await fetch(`${API}/organizations/${org.org_id}/leave`, {
                          method: "POST",
                          headers: {
                            Authorization: `Bearer ${token}`
                          }
                        });
                        if (leaveRes.ok) {
                          setOrganizations(prev => prev.filter(o => o.org_id !== org.org_id));
                          toast.success(`Removed ${org.name} from your profile`);
                        } else {
                          const errData = await leaveRes.json();
                          toast.error(errData.detail || "Failed to remove organization");
                        }
                      } catch (err) {
                        toast.error("Failed to remove organization");
                      }
                    }}
                    className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Add Host Dialog */}
      <Dialog open={showAddOrg} onOpenChange={setShowAddOrg}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Add Host
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Host Name *
              </label>
              <Input
                value={newOrgData.name}
                onChange={(e) => setNewOrgData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., My Company Inc."
                className="h-10"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Description
              </label>
              <Input
                value={newOrgData.description}
                onChange={(e) => setNewOrgData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of your organization"
                className="h-10"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Website
              </label>
              <Input
                value={newOrgData.website}
                onChange={(e) => setNewOrgData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://example.com"
                className="h-10"
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> This organization will become your primary host profile and will appear on all events you host.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowAddOrg(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateOrganization}
                disabled={creatingOrg || !newOrgData.name.trim()}
                className="flex-1 bg-black hover:bg-gray-800"
              >
                {creatingOrg ? (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </div>
                ) : (
                  "Create Organization"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Integrations Section - HIDDEN until finalized */}
      {/* 
      <Card className="rounded-xl border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4 text-purple-500" />
            Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <IntegrationCard
            name="Instagram"
            icon={Instagram}
            connected={user?.instagram_verified}
            username={user?.instagram_username}
            description="Sync inspo from your DMs"
            onConnect={() => setEditingSection('instagram')}
            onDisconnect={() => {}}
          />
          <IntegrationCard
            name="Tap In"
            icon={Music}
            connected={false}
            description="Share your music taste at events"
            comingSoon
            coverImage={TAP_IN_COVER}
          />
        </CardContent>
      </Card>
      */}
      </>)}

      {/* Portal Quick Access removed - handled by QuickAccessPortals in UserProfilePage */}

      {/* Host Experience / Provider CTA */}
      <ProviderWaitlistCard 
        user={user} 
        onDismiss={() => setShowProviderCard(false)} 
        onJoinWaitlist={handleJoinWaitlist}
        meTemplate={meTemplate}
      />

      {/* Edit About Modal */}
      <Dialog open={editingSection === 'about'} onOpenChange={() => setEditingSection(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-500" />
              Edit Profile
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">First Name *</label>
                <Input
                  value={aboutData.first_name}
                  onChange={(e) => {
                    const firstName = e.target.value;
                    const fullName = `${firstName} ${aboutData.last_name}`.trim();
                    setAboutData({...aboutData, first_name: firstName, name: fullName});
                  }}
                  placeholder="First name"
                  data-testid="settings-first-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Last Name *</label>
                <Input
                  value={aboutData.last_name}
                  onChange={(e) => {
                    const lastName = e.target.value;
                    const fullName = `${aboutData.first_name} ${lastName}`.trim();
                    setAboutData({...aboutData, last_name: lastName, name: fullName});
                  }}
                  placeholder="Last name"
                  data-testid="settings-last-name"
                />
              </div>
            </div>

            {/* City with autocomplete */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">City</label>
              {/* Iter 9.45 - Google Places autocomplete, captures structured location for Launch Control */}
              <GooglePlacesInput
                mode="city"
                value={aboutData.city || ""}
                onChange={(city) => setAboutData({...aboutData, city})}
                onSelect={(place) => setAboutData({
                  ...aboutData,
                  city: place.formatted || place.city || "",
                  city_name: place.city,
                  state: place.state_code || place.state,
                  country: place.country_code || place.country,
                  place_id: place.place_id,
                  lat: place.lat,
                  lng: place.lng,
                })}
                dataTestId="me-settings-city"
              />
            </div>

            {/* Company / Organization - Select from synced orgs or type custom */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Company / Org</label>
                <div className="space-y-1.5">
                  {organizations.length > 0 ? (
                    <select
                      value={aboutData.company}
                      onChange={(e) => setAboutData({...aboutData, company: e.target.value})}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="company-org-select"
                    >
                      <option value="">Select from synced orgs...</option>
                      {organizations.map(org => (
                        <option key={org.org_id} value={org.name}>
                          {org.name}{user?.org_id === org.org_id ? ' (Primary)' : ''}
                        </option>
                      ))}
                      {aboutData.company && !organizations.find(o => o.name === aboutData.company) && (
                        <option value={aboutData.company}>{aboutData.company} (custom)</option>
                      )}
                    </select>
                  ) : null}
                  <Input
                    value={aboutData.company}
                    onChange={(e) => setAboutData({...aboutData, company: e.target.value})}
                    placeholder={organizations.length > 0 ? "Or type a custom name" : "Company name"}
                    className="text-sm"
                    data-testid="company-custom-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Title</label>
                <Input
                  value={aboutData.title}
                  onChange={(e) => setAboutData({...aboutData, title: e.target.value})}
                  placeholder="Job title"
                />
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">What best describes you?</label>
              {roleOptionsLoading ? (
                <div className="flex items-center gap-2 text-gray-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading options...</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {roleOptions.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setAboutData({...aboutData, role_type: option.id})}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        aboutData.role_type === option.id
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid={`role-option-${option.id}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Social Handles - Personal */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-gray-700 block mb-1">Personal Social Handles</label>
              <p className="text-xs text-gray-400 mb-3">Your personal accounts</p>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-4 h-4 text-white" />
                  </div>
                  <Input
                    value={aboutData.personal_socials?.instagram || aboutData.instagram || ""}
                    onChange={(e) => setAboutData({
                      ...aboutData, 
                      instagram: e.target.value.replace('@', ''),
                      personal_socials: { ...(aboutData.personal_socials || {}), instagram: e.target.value.replace('@', '') }
                    })}
                    placeholder="personal_instagram"
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <Input
                    value={aboutData.personal_socials?.tiktok || aboutData.tiktok || ""}
                    onChange={(e) => setAboutData({
                      ...aboutData,
                      tiktok: e.target.value.replace('@', ''),
                      personal_socials: { ...(aboutData.personal_socials || {}), tiktok: e.target.value.replace('@', '') }
                    })}
                    placeholder="personal_tiktok"
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Linkedin className="w-4 h-4 text-white" />
                  </div>
                  <Input
                    value={aboutData.personal_socials?.linkedin || aboutData.linkedin || ""}
                    onChange={(e) => setAboutData({
                      ...aboutData,
                      linkedin: e.target.value,
                      personal_socials: { ...(aboutData.personal_socials || {}), linkedin: e.target.value }
                    })}
                    placeholder="personal_linkedin"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Social Handles - Business */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-gray-700 block mb-1">Business Social Handles</label>
              <p className="text-xs text-gray-400 mb-3">Your business or brand accounts</p>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-4 h-4 text-white" />
                  </div>
                  <Input
                    value={aboutData.business_socials?.instagram || ""}
                    onChange={(e) => setAboutData({
                      ...aboutData,
                      business_socials: { ...(aboutData.business_socials || {}), instagram: e.target.value.replace('@', '') }
                    })}
                    placeholder="business_instagram"
                    className="flex-1"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-white" />
                  </div>
                  <Input
                    value={aboutData.business_socials?.website || ""}
                    onChange={(e) => setAboutData({
                      ...aboutData,
                      business_socials: { ...(aboutData.business_socials || {}), website: e.target.value }
                    })}
                    placeholder="https://yourwebsite.com"
                    className="flex-1"
                  />
                </div>

                {/* TikTok business handle (R25: replaces stale Yelp/Google business URL fields) */}
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <Input
                    value={aboutData.business_socials?.tiktok || ""}
                    onChange={(e) => setAboutData({
                      ...aboutData,
                      business_socials: { ...(aboutData.business_socials || {}), tiktok: e.target.value.replace('@', '') }
                    })}
                    placeholder="business_tiktok"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditingSection(null)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSaveAbout} disabled={saving} className="flex-1">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog open={editingSection === 'contact'} onOpenChange={() => setEditingSection(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-500" />
              Edit Contact Info
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Emails */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Email Addresses ({emails.length}/5)
              </label>
              <div className="space-y-2 mb-3">
                {emails.map((email, i) => (
                  <EmailItem
                    key={email}
                    email={email}
                    isPrimary={i === 0}
                    onRemove={() => removeEmail(email)}
                    onSetPrimary={() => setPrimaryEmail(email)}
                    canRemove={emails.length > 1}
                  />
                ))}
              </div>
              {emails.length < 5 && (
                <div className="space-y-3">
                  {/* Gmail Auth Only - Add via Google Sign-in */}
                  <button
                    onClick={() => {
                      // Store the ORIGINAL session token before OAuth redirect
                      // This is crucial - OAuth will create a new session and overwrite the current one
                      const currentToken = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
                      if (currentToken) {
                        localStorage.setItem('original_session_token', currentToken);
                      }
                      localStorage.setItem('pending_add_email_flow', 'true');
                      const redirectUrl = window.location.origin + '/me?tab=settings&email_added=true';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Add Gmail Account</span>
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    Sign in with Google to add and verify your Gmail address.
                    By adding an email, you agree to our <a href="https://phygital.systems/terms" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Terms</a> and <a href="https://phygital.systems/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Privacy Policy</a>, and Beehiiv's <a href="https://www.beehiiv.com/tou" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Terms</a>.
                  </p>
                </div>
              )}
            </div>

            {/* Phones */}
            <div className="pt-3 border-t">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Phone Numbers ({phones.length}/3)
              </label>
              <div className="space-y-2 mb-3">
                {phones.map((phone, i) => (
                  <PhoneItem
                    key={phone}
                    phone={phone}
                    isPrimary={i === 0}
                    onRemove={() => removePhone(phone)}
                    onSetPrimary={() => setPrimaryPhone(phone)}
                    canRemove={phones.length > 1}
                  />
                ))}
              </div>
              {phones.length < 3 && (
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="Add phone number"
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && addPhone()}
                  />
                  <Button variant="outline" onClick={addPhone} className="px-3">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditingSection(null)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSaveAbout} disabled={saving} className="flex-1">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phone Verification Modal (Sinch SMS) */}
      <Dialog open={showPhoneVerify} onOpenChange={() => { setShowPhoneVerify(false); setVerifyCode(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-purple-500" />
              Verify Phone Number
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              We sent a verification code to <strong>{pendingPhone}</strong>
            </p>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Enter Code</label>
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>
            <p className="text-xs text-gray-500 text-center">
              Verification powered by Sinch SMS
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setShowPhoneVerify(false); setVerifyCode(""); }} className="flex-1">
              Cancel
            </Button>
            <Button onClick={verifyPhoneCode} disabled={verifying || verifyCode.length < 6} className="flex-1">
              {verifying ? "Verifying..." : "Verify"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Early Access / Provider Waitlist Modal */}
      <EarlyAccessModal 
        open={showEarlyAccessModal} 
        onOpenChange={setShowEarlyAccessModal} 
      />

      {/* Edit Host Modal */}
      <Dialog open={!!editingOrg} onOpenChange={() => setEditingOrg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-purple-500" />
              Edit Host
            </DialogTitle>
          </DialogHeader>
          
          {editingOrg && (
            <div className="space-y-4 py-4">
              {/* Logo Upload */}
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <div className="w-20 h-20 rounded-xl bg-black flex items-center justify-center overflow-hidden">
                    {editingOrg.logo ? (
                      <img src={editingOrg.logo} alt={editingOrg.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-2xl font-bold">
                        {orgFormData.name?.charAt(0)?.toUpperCase() || "O"}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => orgLogoInputRef.current?.click()}
                    disabled={orgLogoUploading}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <Camera className={`w-6 h-6 text-white ${orgLogoUploading ? 'animate-pulse' : ''}`} />
                  </button>
                  <input
                    ref={orgLogoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleOrgLogoUpload(e, editingOrg.org_id)}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Host Logo</p>
                  <p className="text-xs text-gray-500">Click to upload a new logo</p>
                </div>
              </div>

              {/* Organization Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Name</label>
                <Input
                  value={orgFormData.name}
                  onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                  placeholder="Host name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
                <textarea
                  value={orgFormData.description}
                  onChange={(e) => setOrgFormData({ ...orgFormData, description: e.target.value })}
                  placeholder="Brief description of your organization"
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Website</label>
                <Input
                  value={orgFormData.website || ""}
                  onChange={(e) => setOrgFormData({ ...orgFormData, website: e.target.value })}
                  placeholder="https://yourwebsite.com"
                />
              </div>

              {/* Role Info */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Your Role</p>
                <p className="text-sm font-medium text-purple-600 capitalize">{editingOrg.my_role || "Member"}</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditingOrg(null)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSaveOrganization} disabled={saving} className="flex-1">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsTab;
